import { PluginConfigInterface } from '@cbsinteractive/avia-js';
import { TKeyValuePair } from 'library/ts/types/TKeyValuePair';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IImportsAvia } from 'tag/ts/tag/interfaces/config/imports/IImportsAvia';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description Base class for all VTG Avia plugins implementing common plugin infrastructure
 *
 * **Plugin System Foundation:**
 * Provides standardized infrastructure for all VTG Avia plugins, including lifecycle
 * management, configuration generation, and Smart Tag integration. All specific plugins
 * (Shaka, HLS, GAM, Tracking, UI) extend this base class.
 *
 * **Plugin Architecture Pattern:**
 * - **Conditional Loading**: `shouldBeUsed()` determines if plugin loads based on business logic
 * - **Configuration Generation**: `getFactoryParams()` provides VTG-compatible plugin config
 * - **Namespace Management**: Handles plugin loading from DOM or NPM package sources
 * - **Smart Tag Integration**: Provides access to Tag business logic and utilities
 *
 * **Plugin Configuration System:**
 * - **Package Management**: Loads plugin code from NPM packages or DOM namespaces
 * - **Namespace Resolution**: Maps Smart Tag config to VTG plugin namespaces
 * - **Factory Pattern**: Generates VTG-compatible plugin configurations
 * - **Error Handling**: Graceful fallback when plugins are unavailable
 *
 * **Lifecycle Methods:**
 * - **shouldBeUsed()**: Business logic determining plugin activation
 * - **getFactoryParams()**: VTG plugin configuration generation
 * - **generateConfig()**: Complete VTG plugin config assembly
 * - **isReinitializable()**: Declares whether this plugin can be safely destroyed and
 *   recreated with fresh params while the underlying avia-js player instance stays alive
 *   (e.g. AviaLifecycleManager's stream-swap reuse path, PLBCK-8461). Defaults to `false`.
 *
 * **Plugin Loading Strategy:**
 * Supports both development (DOM-loaded) and production (NPM package) plugin sources,
 * enabling flexible deployment and testing across different environments.
 *
 * **Business Impact:**
 * Foundation for all VTG plugin integration, enabling modular video functionality
 * while maintaining Smart Tag's business logic control over plugin activation.
 */
export abstract class AviaPlugin extends AbstractTagClass implements IAviaPlugin {
	package;

	protected constructor( argument: ITagAbstractClassArgument ) {
		super( argument );
	}

	get namespaceBase(): IImportsAvia {
		return this.tag?.model?.config?.tag?.Imports?.params?.avia;
	}

	namespaceKey: string = 'plugin_avia';

	get namespaceComplete(): string {
		return this.namespaceBase?.[ this.namespaceKey ]?.namespace;
	}

	get loadedPlugin(): TPluginConfigFactory {
		return this.getPluginIfAlreadyLoaded( this.namespaceComplete );
	}

	shouldBeUsed(): boolean {
		return true;
	}

	isReinitializable(): boolean {
		return false;
	}

	getFactoryParams(): TKeyValuePair {
		this?.logger?.log( 'getFactoryParams base' );
		return {
			debug: this.runtimeVO?.debug?.player
		};
	}

	getPluginIfAlreadyLoaded( namespace: string ): TPluginConfigFactory {
		// sanity check
		if ( this.utils.Type.isEmpty( namespace ) ) {
			return undefined;
		}
		if ( this.utils.Type.isString( namespace ) ) {
			// get namespace reference from DOM
			const nsObject = this.utils.Object.getDeepVal( window, namespace );
			// check if our namespace exists
			switch ( this.utils.Type.isNullOrUndefined( nsObject ) ) {
				case true:
					return undefined;
				case false:
					return nsObject[ 'plugin' ];
			}
		}
	}

	generateConfig(): PluginConfigInterface<any> {
		const factory = this.getConfigFactory();

		if ( this.utils.Type.isNullOrUndefined( factory ) ) {
			this?.logger?.warn( 'Could not get plugin factory' );
			return undefined;
		}

		const initialized = factory( this.getFactoryParams() );

		return initialized;
	}

	getConfigFactory(): TPluginConfigFactory {
		this?.logger?.log( 'getConfigFactory', this );
		let pluginConfig = undefined;

		const loadedPlugin = this.loadedPlugin;
		this?.logger?.log( 'loadedPlugin', loadedPlugin );

		// Provide the plugin config if one already exists in DOM
		if ( !this.utils.Type.isNullOrUndefined( loadedPlugin ) ) {
			this?.logger?.log( 'getConfig', 'loadedPlugin:', loadedPlugin );
			pluginConfig = loadedPlugin;
		}
		// no DOM config found, provide package version instead
		else if ( !this.utils.Type.isNullOrUndefined( this.package ) ) {
			this?.logger?.log( 'getConfig', 'this.package:', this.package );
			pluginConfig = this.package;
		}

		// Send warning if no plugin config can be provided.
		if ( this.utils.Type.isNullOrUndefined( pluginConfig ) ) {
			this?.logger?.warn( 'Plugin was neither imported nor loaded', this.namespaceComplete, this.package );
		}

		return pluginConfig;
	}
}